<div class="scheduling-content">
  <!-- Backdrop for closing dropdown -->
  <div 
    class="timezone-backdrop" 
    *ngIf="isTimezoneDropdownOpen"
    (click)="closeTimezoneDropdown()"
  ></div>
  
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-lg-8 col-12">
        <div class="scheduling-wrapper">
          <!-- Header -->
          <div class="scheduling-header mb-4">
            <span class="form-label-header">MEETING REQUEST FORM</span>
            <h1 class="scheduling-title">We look forward to meeting with you</h1>
          </div>

          <!-- Date and Time Selection -->
          <div class="datetime-section mb-5">
            <h3 class="section-title">
              Select a date and time <span class="required">*</span>
            </h3>

            <!-- Selected Date Display -->
            <div class="selected-date-display" *ngIf="selectedDate">
              {{ getFormattedSelectedDate() }}
            </div>

            <!-- Calendar -->
            <div class="calendar-container">
              <div class="calendar-header">
                <button 
                  type="button" 
                  class="btn calendar-nav-btn"
                  (click)="previousMonth()"
                  aria-label="Previous month"
                >
                  <i class="bi bi-chevron-left"></i>
                </button>
                <h4 class="calendar-month">{{ getMonthName() }}</h4>
                <button 
                  type="button" 
                  class="btn calendar-nav-btn"
                  (click)="nextMonth()"
                  aria-label="Next month"
                >
                  <i class="bi bi-chevron-right"></i>
                </button>
              </div>

              <div class="calendar-grid">
                <div class="calendar-weekdays">
                  <div class="weekday">Mon</div>
                  <div class="weekday">Tue</div>
                  <div class="weekday">Wed</div>
                  <div class="weekday">Thu</div>
                  <div class="weekday">Fri</div>
                  <div class="weekday">Sat</div>
                  <div class="weekday">Sun</div>
                </div>
                <div class="calendar-days">
                  <button
                    *ngFor="let day of getCalendarDays()"
                    type="button"
                    class="calendar-day"
                    [class.past]="day && isPastDate(day)"
                    [class.selected]="day && isSelectedDate(day)"
                    [disabled]="!day || isPastDate(day)"
                    (click)="day && selectDate(day)"
                  >
                    {{ day?.getDate() || '' }}
                  </button>
                </div>
              </div>
            </div>

            <!-- Time Slots -->
            <div class="time-slots-container" *ngIf="selectedDate">
              <div class="time-slots" *ngIf="hasAvailableSlots(); else noSlots">
                <button
                  *ngFor="let slot of getAvailableTimeSlots()"
                  type="button"
                  class="time-slot"
                  [class.selected]="schedulingForm.selectedTime === slot.time"
                  [class.unavailable]="!slot.available"
                  [disabled]="!slot.available"
                  (click)="selectTime(slot)"
                >
                  {{ slot.time }}
                </button>
              </div>
              <ng-template #noSlots>
                <div class="no-slots">
                  <i class="bi bi-calendar-x"></i>
                  <p>No availability, try another day</p>
                </div>
              </ng-template>
            </div>

            <!-- Timezone Selector -->
            <div class="timezone-selector" (click)="onTimezoneDropdownClick($event)">
              <div class="timezone-button-wrapper">
                <button 
                  type="button" 
                  class="btn timezone-btn"
                  (click)="toggleTimezoneDropdown()"
                  [attr.aria-expanded]="isTimezoneDropdownOpen"
                >
                  <i class="bi bi-globe me-2"></i>
                  {{ getSelectedTimezoneLabel() }}
                  <i class="bi bi-chevron-down ms-2" [class.rotated]="isTimezoneDropdownOpen"></i>
                </button>
              </div>
              
              <div 
                class="timezone-dropdown-container" 
                *ngIf="isTimezoneDropdownOpen"
                (click)="onTimezoneDropdownClick($event)"
              >
                <div class="timezone-search-wrapper">
                  <i class="bi bi-search timezone-search-icon"></i>
                  <input 
                    type="text" 
                    id="timezone-search"
                    class="timezone-search-input"
                    placeholder="Search timezones..."
                    [(ngModel)]="timezoneSearchTerm"
                    (input)="onTimezoneSearch()"
                  />
                </div>
                
                <div class="timezone-list">
                  <button
                    *ngFor="let tz of filteredTimezones"
                    type="button"
                    class="timezone-option"
                    [class.active]="schedulingForm.timezone === tz.value"
                    (click)="selectTimezone(tz)"
                  >
                    {{ tz.label }}
                  </button>
                  
                  <div class="no-results" *ngIf="filteredTimezones.length === 0">
                    <i class="bi bi-search"></i>
                    <p>No timezones found</p>
                  </div>
                </div>
              </div>
            </div>

            <!-- Date & Time Error -->
            <div class="error-message" *ngIf="hasError('selectedDate')">
              <i class="bi bi-exclamation-circle me-1"></i>
              {{ getErrorMessage('selectedDate') }}
            </div>
          </div>

          <!-- Contact Form -->
          <form class="scheduling-form" (ngSubmit)="onSubmit()">
            <!-- Name -->
            <div class="form-group mb-4">
              <label for="name" class="form-label">
                Name <span class="required">*</span>
              </label>
              <input 
                type="text" 
                id="name"
                name="name"
                class="form-control form-input"
                [class.error]="hasError('name')"
                placeholder="Jane Smith"
                [(ngModel)]="schedulingForm.name"
                (input)="onFieldChange('name')"
                required
              />
              <div class="error-message" *ngIf="hasError('name')">
                <i class="bi bi-exclamation-circle me-1"></i>
                {{ getErrorMessage('name') }}
              </div>
            </div>

            <!-- Email -->
            <div class="form-group mb-4">
              <label for="email" class="form-label">
                Email address <span class="required">*</span>
              </label>
              <input 
                type="email" 
                id="email"
                name="email"
                class="form-control form-input"
                [class.error]="hasError('email')"
                placeholder="email@website.com"
                [(ngModel)]="schedulingForm.email"
                (input)="onFieldChange('email')"
                required
              />
              <div class="error-message" *ngIf="hasError('email')">
                <i class="bi bi-exclamation-circle me-1"></i>
                {{ getErrorMessage('email') }}
              </div>
            </div>

            <!-- Phone -->
            <div class="form-group mb-4">
              <label for="phone" class="form-label">
                Phone number <span class="required">*</span>
              </label>
              <input 
                type="tel" 
                id="phone"
                name="phone"
                class="form-control form-input"
                [class.error]="hasError('phone')"
                placeholder="555-555-5555"
                [(ngModel)]="schedulingForm.phone"
                (input)="onFieldChange('phone')"
                (blur)="onFieldBlur('phone')"
                required
              />
              <div class="error-message" *ngIf="hasError('phone')">
                <i class="bi bi-exclamation-circle me-1"></i>
                {{ getErrorMessage('phone') }}
              </div>
            </div>

            <!-- Services -->
            <div class="form-group mb-4">
              <label class="form-label">
                Which service or services are you interested in? <span class="required">*</span>
              </label>
              <div class="checkbox-group">
                <div 
                  id="services"
                  class="form-check mb-2" 
                  *ngFor="let service of serviceOptions"
                >
                  <input 
                    type="checkbox" 
                    class="form-check-input"
                    [class.error]="hasError('services')"
                    [id]="'service-' + service"
                    [checked]="isServiceSelected(service)"
                    (change)="onServiceChange(service, $event)"
                  />
                  <label class="form-check-label" [for]="'service-' + service">
                    {{ service }}
                  </label>
                </div>
              </div>
              <div class="error-message" *ngIf="hasError('services')">
                <i class="bi bi-exclamation-circle me-1"></i>
                {{ getErrorMessage('services') }}
              </div>
            </div>

            <!-- Message -->
            <div class="form-group mb-4">
              <label for="message" class="form-label">Message</label>
              <textarea 
                id="message"
                name="message"
                class="form-control form-textarea"
                rows="5"
                placeholder="Tell us about your project..."
                [(ngModel)]="schedulingForm.message"
              ></textarea>
            </div>

            <!-- Honeypot field - hidden from users -->
            <div class="honeypot-field">
              <label for="company" class="visually-hidden">Company (leave blank)</label>
              <input 
                type="text" 
                id="company"
                name="company"
                class="form-control"
                [(ngModel)]="schedulingForm.honeypot"
                tabindex="-1"
                autocomplete="off"
              />
            </div>

            <!-- Submit Button -->
            <div class="form-group">
              <button 
                type="submit" 
                class="btn submit-btn w-100"
                [disabled]="isSubmitDisabled()"
                [class.loading]="isSubmitting"
              >
                {{ getSubmitButtonText() }}
              </button>

              <div class="submit-message mt-3" *ngIf="submitMessage" [class.success]="!isSubmitting">
                {{ submitMessage }}
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</div>